/* ============================================================================
   TEMAS DÍA Y NOCHE

   No es una preferencia estética. En vuelo nocturno el ojo tarda entre veinte
   y treinta minutos en adaptarse a la oscuridad, y **un solo destello de luz
   blanca o azul destruye esa adaptación en un segundo**. Después hay que
   esperar otra vez.

   Un piloto que mira una pantalla brillante y azulada y después mira afuera no
   ve el terreno. Esa es la razón por la que las cabinas usan luz roja o ámbar
   tenue, y por la que este módulo existe.

   ══════════════════════════════════════════════════════════════════════════
   LAS TRES REGLAS DEL TEMA NOCTURNO

   1. **Nada de azul ni de cian.** El azul es lo que más castiga la adaptación
      nocturna: los bastones de la retina son más sensibles al extremo azul del
      espectro. En noche, el cian de la aplicación pasa a ámbar.

   2. **Brillo máximo acotado.** Ningún color del tema nocturno pasa de un
      brillo relativo moderado. Un blanco puro a plena luminancia en una cabina
      a oscuras es exactamente el destello que hay que evitar.

   3. **Rojo y amarillo NO se apagan.** La precaución y el peligro tienen que
      seguir saltando a la vista: se atenúan menos que el resto, porque un
      aviso que no se ve no es un aviso. Es la única excepción a la regla 2, y
      es deliberada.
   ══════════════════════════════════════════════════════════════════════════

   El tema DIURNO es el opuesto: alto contraste contra la luz que entra por la
   burbuja, donde el problema no es deslumbrar sino que no se lea nada.
   ========================================================================== */

/** Los temas que existen. `auto` decide por la hora. */
export const TEMAS = [
  ["dia", "Día"],
  ["noche", "Noche"],
  ["auto", "Automático"],
];

export const TEMA_POR_DEFECTO = "auto";

/**
 * Paleta diurna: alto contraste.
 *
 * En vuelo diurno con burbuja de policarbonato entra mucha luz y el problema
 * es el opuesto al nocturno — no deslumbrar, sino que se lea algo.
 */
export const PALETA_DIA = {
  "--hc-bg": "#0C1116",
  "--hc-panel": "#161D25",
  "--hc-panel-alt": "#1D2732",
  "--hc-hairline": "#2B3641",
  "--hc-text": "#F2F7FB",
  "--hc-muted": "#A6B5C4",
  "--hc-amber": "#F2A93B",
  "--hc-cyan": "#5BD1D7",
  "--hc-ok": "#4CAF7D",
  "--hc-caution": "#FFD23F",
  "--hc-danger": "#E4573D",
};

/**
 * Paleta nocturna: ámbar sobre negro, sin azul.
 *
 * El cian se reemplaza por ámbar, el verde se corre hacia el oliva, y todo
 * baja de brillo salvo precaución y peligro.
 */
export const PALETA_NOCHE = {
  "--hc-bg": "#000000",
  "--hc-panel": "#0B0906",
  "--hc-panel-alt": "#141009",
  "--hc-hairline": "#2A2114",
  /* Ámbar tenue en vez de blanco: el texto es lo que más superficie ocupa y
     por lo tanto lo que más luz emite. */
  /* Ámbar tenue, no dorado: la primera versión (#D9A441) brillaba MÁS que el
     rojo de peligro, así que un aviso grave se perdía entre los datos. Lo
     encontró la prueba de que los avisos destaquen. */
  "--hc-text": "#A8761F",
  "--hc-muted": "#6B4F1E",
  "--hc-amber": "#A8761F",
  /* El cian NO existe de noche. Se mapea a ámbar más claro para conservar la
     jerarquía visual sin meter azul. */
  "--hc-cyan": "#C08A2E",
  /* El verde se corre al oliva: menos azul, menos brillo. */
  "--hc-ok": "#7A8F3C",
  /* Precaución y peligro se atenúan MENOS: un aviso que no se ve no es un
     aviso. Es la excepción deliberada a la regla del brillo. */
  "--hc-caution": "#FFC933",
  /* El rojo se aclara respecto del diurno para que su BRILLO supere al del
     texto: un rojo oscuro sobre negro, con el texto ámbar más claro al lado,
     se lee como menos importante que un dato cualquiera. */
  "--hc-danger": "#FF7A5C",
};

/** Componentes de un color `#RRGGBB`, o `null` si no lo es. */
export function componentes(hex) {
  if (typeof hex !== "string") return null;
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/**
 * Brillo relativo percibido, de 0 a 1.
 *
 * Coeficientes de luminancia: el ojo ve el verde mucho más brillante que el
 * azul con la misma intensidad. Usar el promedio de los tres daría que un azul
 * saturado es «medio», cuando en pantalla oscura casi no se ve.
 */
export function brillo(hex) {
  const c = componentes(hex);
  if (!c) return null;
  return (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255;
}

/**
 * Cuánto azul tiene un color, respecto de su componente dominante.
 *
 * Lo que castiga la adaptación nocturna no es el valor absoluto de azul sino
 * que el color SEA azulado: un gris tiene azul alto y no molesta.
 */
export function dominanciaAzul(hex) {
  const c = componentes(hex);
  if (!c) return null;
  const max = Math.max(c.r, c.g, c.b);
  if (max === 0) return 0;
  /* Cuánto excede el azul al rojo, normalizado. Positivo = azulado. */
  return Math.max(0, (c.b - c.r) / max);
}

/**
 * ¿Es de noche en este lugar y a esta hora?
 *
 * Aproximación deliberadamente simple: entre las 18 y las 6 hora local. No se
 * calcula el orto y el ocaso reales porque el usuario SIEMPRE puede forzar el
 * tema a mano — y un cálculo astronómico que se equivoque por media hora sería
 * más molesto que útil.
 */
export function esDeNoche(fecha = new Date()) {
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  if (Number.isNaN(d.getTime())) return false;
  const h = d.getHours();
  return h >= 18 || h < 6;
}

/**
 * Qué paleta corresponde.
 *
 * `auto` decide por la hora; `dia` y `noche` mandan sobre eso. Un tema elegido
 * a mano NUNCA se cambia solo: el piloto que puso noche a las 3 de la tarde
 * porque va a entrar en un cañón sabe por qué lo hizo.
 */
export function paletaDe(tema, fecha = new Date()) {
  if (tema === "dia") return PALETA_DIA;
  if (tema === "noche") return PALETA_NOCHE;
  return esDeNoche(fecha) ? PALETA_NOCHE : PALETA_DIA;
}

/** El nombre del tema que se está aplicando de verdad. */
export function temaEfectivo(tema, fecha = new Date()) {
  if (tema === "dia" || tema === "noche") return tema;
  return esDeNoche(fecha) ? "noche" : "dia";
}

/**
 * Aplica la paleta al documento.
 *
 * Se escriben las variables en el elemento raíz: toda la aplicación las usa,
 * así que no hace falta tocar ni un componente. Devuelve el tema aplicado.
 */
export function aplicarTema(tema, { documento = globalThis.document, fecha = new Date() } = {}) {
  const efectivo = temaEfectivo(tema, fecha);
  const paleta = paletaDe(tema, fecha);

  const raiz = documento?.documentElement;
  if (!raiz?.style) return efectivo;

  for (const [variable, valor] of Object.entries(paleta)) {
    raiz.style.setProperty(variable, valor);
  }

  /* Un atributo en la raíz, para que el CSS pueda ajustar lo que no son
     colores: grosores de línea, sombras, opacidades. */
  raiz.setAttribute("data-tema", efectivo);
  return efectivo;
}
